import { cloneDeep } from "lodash";
import { FilterOptions } from "../../../common/types/roots/FilterOptions";
import { Facade, TableDataAndJournalParams } from "../../../common/Facade";
import { FilterSettings } from "../../../common/types/roots/FilterSettings";
import { FilterSettingsForm } from "../../../common/types/roots/FilterSettingsForm";
import { loanStatusFilterOptions } from "../loan-status";
import { LoanStatusFilterSettingsForm } from "../settings-form/LoanStatusFilterSettingsForm";
import { LoanStatusFilterSettings } from "../settings/LoanStatusFilterSettings";
import { LoanStatusTableDataRequest } from "../data-request/LoanStatusTableDataRequest";
import { LoanStatusJournalRequest } from "../journal/LoanStatusJournalRequest";
import { LoanStatusRow } from "../table-data/LoanStatusRow";
import { LoanStatusRowData } from "../table-data/LoanStatusRowData";
import { LoanStatusResponseRow } from "../other-types/LoanStatusResponseRow";
import moment from "moment";

export class LoanStatusFacade implements Facade {
    getFilterOptions(): FilterOptions {
        return cloneDeep(loanStatusFilterOptions)
    }

    settingsToForm(settings: FilterSettings, options: FilterOptions): FilterSettingsForm {
        // 1. Валидаций
        if (settings.reportName !== 'LOAN-STATUS') {
            throw new Error('error_type_filter_settings')
        }
        if (options.key !== 'LOAN-STATUS') {
            throw new Error('error_type_filter_options')
        }

        let reportName = options.reportNames.find(it => it.value === settings.reportName)
        if (!reportName) {
            throw new Error('error_not_found_report_name')
        }
        reportName = { ...reportName }

        let reportType = options.reportTypes.find(it => it.value === settings.reportType)!
        if (!reportType) {
            throw new Error('error_not_found_report_type')
        }
        reportType = { ...reportType }

        let classificationType = options.classificationTypes.find(it => it.value === settings.classificationType)
        if (!classificationType) {
            throw new Error('error_not_found_class_type')
        }
        classificationType = { ...classificationType }

        let dataSource = options.dataSources.find(it => it.value === settings.dataSource)
        if (!dataSource) {
            throw new Error('error_not_found_data_source')
        }
        dataSource = { ...dataSource }

        let periodicity = options.periodicities.find(it => it.value === settings.periodicity)
        if (!periodicity) {
            throw new Error('error_not_found_periodicity')
        }
        periodicity = { ...periodicity }

        const budgetTypeOption = cloneDeep(options
            .budgetTypes
            .find(it => it.value === settings.budgetType)
            ?? null)

        const creditorOption = cloneDeep(options
            .creditors
            .find(it => it.value === settings.creditor)
            ?? null)

        const administratorOption = cloneDeep(options
            .administrators
            .find(it => it.value === settings.administrator)
            ?? null)

        const contractorOption = cloneDeep(options
            .contractors
            .find(it => it.value === settings.contractor)
            ?? null)

        const loanPurposeOption = cloneDeep(options
            .loanPurposes
            .find(it => it.value === settings.contractor)
            ?? null)

        const agreementStatusOption = cloneDeep(options
            .agreementStatuses
            .find(it => it.value === settings.agreementStatus)
            ?? null)

        const regionOption = cloneDeep(options
            .regions
            .find(it => it.value === settings.region)
            ?? null)

        // 2. Формирование
        const form: LoanStatusFilterSettingsForm = {
            // Base:
            key: 'LOAN-STATUS',
            reportName: reportName,
            reportType: reportType,
            classificationType: classificationType,
            dataSource: dataSource,
            periodicity: periodicity,
            // Custom:
            date: settings.date ? new Date(settings.date) : null,
            budgetType: budgetTypeOption,
            region: regionOption,
            creditor: creditorOption,
            administrator: administratorOption,
            contractor: contractorOption,
            loanPurpose: loanPurposeOption,
            agreementStatus: agreementStatusOption,
        }

        return form
    }

    formToSettings(form: FilterSettingsForm): FilterSettings {
        if (form.key !== 'LOAN-STATUS') {
            throw new Error('error_type_filter_settingsForm')
        }

        const strDate = moment(form.date).format('YYYY-MM-DD')

        const settings: LoanStatusFilterSettings = {
            // Base:
            reportName: 'LOAN-STATUS',
            reportType: form.reportType.value,
            classificationType: form.classificationType.value,
            dataSource: form.dataSource.value,
            periodicity: form.periodicity.value,
            // Custom:
            date: strDate,
            budgetType: form.budgetType?.value ?? null,
            region: form.region?.value ?? null,
            creditor: form.creditor?.value ?? null,
            administrator: form.administrator?.value ?? null,
            contractor: form.contractor?.value ?? null,
            loanPurpose: form.loanPurpose?.value ?? null,
            agreementStatus: form.agreementStatus?.value ?? null,
        }

        return settings
    }

    getEmptyForm(): FilterSettingsForm {
        const options = cloneDeep(loanStatusFilterOptions)

        const emptyForm: LoanStatusFilterSettingsForm = {
            key: 'LOAN-STATUS',
            // Base fields
            reportName: options.reportNames.find(it => it.value === 'LOAN-STATUS')!,
            reportType: options.reportTypes.find(it => it.value === 'REGULATED')!,
            classificationType: options.classificationTypes.find(it => it.value === 'MIXED')!,
            dataSource: options.dataSources.find(it => it.value === 'MIXED')!,
            periodicity: options.periodicities.find(it => it.value === 'DAY')!,
            // Custom fields            
            date: null,
            region: null,
            budgetType: null,
            creditor: null,
            administrator: null,
            contractor: null,
            loanPurpose: null,
            agreementStatus: null,
            // roundUp: false,
            // roundUpTo: null,
            // incomes: cloneDeep(allIncomeClassificators),
            // expenses: cloneDeep(reportEibExpensesClassificators)
        }

        return emptyForm
    }

    async constructReport(
        form: FilterSettingsForm,
        templateName: string,
        excelName: string | null,
        userId: string,
    ): Promise<TableDataAndJournalParams> {
        // 1. Валидаций и Формирование данных для таблицы
        if (form.key !== 'LOAN-STATUS') {
            throw new Error('error_type_filter_settingsForm')
        }

        if (!form.date) {
            throw new Error('error_input_date')
        }

        const strDate = moment(form.date).format('YYYY-MM-DD')

        const params: LoanStatusTableDataRequest = {
            date: strDate,
            region: form.region?.value ?? null,
            budget_type: form.budgetType?.value ?? null,
            creditor: form.creditor?.value ?? null,
            administrator: form.administrator?.value ?? null,
            contractor: form.contractor?.value ?? null,
            loan_purpose: form.loanPurpose?.value ?? null,
            status: form.agreementStatus?.value ?? null,
        }

        const response = await fetch('/api-py/monitoring/reports-constructor/LOAN-STATUS',
            {
                method: 'POST',
                body: JSON.stringify(params)
            }
        )
        if (!response.ok) {
            throw new Error('error_api_report_data_failed')
        }
        const responseTableData: {
            tenge_rows: LoanStatusResponseRow[]
            dollar_rows: LoanStatusResponseRow[]
        } = await response.json()

        // 2. Формирование параметров для выгрузки журнал
        const settings: FilterSettings = this.formToSettings(cloneDeep(form))
        const journalParams: LoanStatusJournalRequest = {
            reportName: 'LOAN-STATUS',
            // journal data
            name: templateName,
            excel_name: excelName,
            filter_settings: settings,
            user_id: userId,
            // for excel
            date: strDate,
            region: form.region?.label ?? null,
            budget_level: form.budgetType?.label ?? null,
            creditor: form.creditor?.label ?? null,
            administrator: form.administrator?.label ?? null,
            contractor: form.contractor?.label ?? null,
            loan_purpose: form.loanPurpose?.label ?? null,
            status: form.agreementStatus?.label ?? null,
            tenge_rows: responseTableData.tenge_rows,
            dollar_rows: responseTableData.dollar_rows,
        }

        const tengeRows: LoanStatusRow[] = responseTableData.tenge_rows.map((it, index) => {
            const temp: LoanStatusRow = {
                index: index,
                type: it.type,
                name: it.name,
                yearPrincipalDict: cloneDeep(it.year_principal_dict),
                yearInterestDict: cloneDeep(it.year_interest_dict)
            }
            return temp
        })

        const dollarRows: LoanStatusRow[] = responseTableData.dollar_rows.map((it, index) => {
            const temp: LoanStatusRow = {
                index: index,
                type: it.type,
                name: it.name,
                yearPrincipalDict: cloneDeep(it.year_principal_dict),
                yearInterestDict: cloneDeep(it.year_interest_dict)
            }
            return temp
        })

        const tableData: LoanStatusRowData = {
            tengeRows: tengeRows,
            dollarRows: dollarRows
        }

        const result: TableDataAndJournalParams = {
            tableData: cloneDeep(tableData),
            journalParams: journalParams
        }

        return result
    }
}
